import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwY29uc3RydWN0aW9uJTIwc2l0ZSUyMGFyY2hpdGVjdHVyYWwlMjBzdGVlbCUyMGNvbmNyZXRlJTIwc3RydWN0dXJlfGVufDB8fHx8MTc9MTAyNjk1OHww&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "ELECTRICAL.", cls: "text-[#F3F4F6]" },
  { text: "PLUMBING.", cls: "text-outline" },
  { text: "FURNITURE.", cls: "text-[#FFFFFF]" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Gativan high-rise structural concrete project under construction"
          className="h-[120%] w-full object-cover brightness-[1.1] contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/80 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/65 to-[#0B0C0E]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/60 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 sm:px-8 lg:pb-16">
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-[#FFFFFF]"
          >
            Gativan Office · Contracting &amp; Engineering · Est. 2019
          </motion.p>
        </div>

        <h1 data-testid="hero-headline" className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tight">
          {lines.map((l, i) => (
            <span key={l.text} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3 + i * 0.15, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-[clamp(3rem,10vw,8.5rem)] ${l.cls}`}
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg"
          >
            Three engineering disciplines, one uncompromising standard. From high-voltage switchgear
            to bespoke joinery, Gativan delivers turnkey fit-outs for UK Realty's landmark developments
            across India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="flex flex-wrap gap-4 lg:justify-end"
          >
            <a
              href="#contact"
              data-testid="hero-cta-inquire"
              className="group flex items-center gap-3 rounded-full bg-[#FFFFFF] px-8 py-4 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-black transition-transform duration-200 hover:scale-[1.04] active:scale-95"
            >
              Start a Project
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-1" />
            </a>
            <a
              href="tel:+917709322253"
              data-testid="hero-cta-call"
              className="flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-mono text-sm uppercase tracking-[0.15em] text-[#F3F4F6] backdrop-blur transition-colors hover:border-[#FFFFFF] hover:text-[#FFFFFF]"
            >
              <Phone size={16} /> 77093 22253
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
